"use client";

import type { ReactNode } from "react";
import { useTranslations } from "next-intl";
import { NavButton } from "@repo/ui/nav-button";
import { cn } from "@repo/ui/utils";
import { Link, usePathname } from "@/i18n/navigation";
import type { CategoryNavItem } from "./directory-listing.types";

interface CategoryNavLinksProps {
  items: CategoryNavItem[];
  /**
   * When set, renders a leading "All" link to the listing index and marks it
   * active when the current route is that index.
   */
  allHref?: string;
}

/**
 * A single pill-styled navigation link. Reuses the shared {@link NavButton}
 * primitive (`asChild` renders the child link), with the brand-token active /
 * hover colours and desktop scale-up matching the SelectorTab pills it replaces
 * in the directory filter bar.
 */
function NavTabLink({
  href,
  active,
  children,
}: {
  href: string;
  active: boolean;
  children: ReactNode;
}) {
  return (
    <NavButton
      asChild
      variant="item"
      size="sm"
      active={active}
      className={cn(
        "shrink-0 desktop:px-4 desktop:py-3 desktop:typography-component-sm-medium",
        active
          ? "bg-button-primary-background text-button-primary-text"
          : "text-text-primary hover:bg-surface-base-secondary",
      )}
    >
      <Link href={href} aria-current={active ? "page" : undefined}>
        {children}
      </Link>
    </NavButton>
  );
}

/**
 * Editor-curated category navigation: a row of links to the top-level category
 * archive routes (`/<category-slug>`), with the active link reflecting the
 * current archive route. Replaces the Algolia refinement pills for articles —
 * these navigate to archive pages rather than filtering in place.
 *
 * `usePathname` returns the locale-stripped path (e.g. `/food`) and category
 * slugs are stored with a leading slash, so they compare directly.
 */
export function CategoryNavLinks({ items, allHref }: CategoryNavLinksProps) {
  const t = useTranslations("directory");
  const pathname = usePathname();

  return (
    <>
      {allHref && (
        <NavTabLink href={allHref} active={pathname === allHref}>
          {t("all")}
        </NavTabLink>
      )}
      {items.map((item) => (
        <NavTabLink
          key={item.slug}
          href={item.slug}
          active={pathname === item.slug}
        >
          {item.label}
        </NavTabLink>
      ))}
    </>
  );
}
